import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { currencies, convertFromGBP, formatPrice as formatPriceUtil, getCurrency, getCurrencyByCountry } from '../data/currencies';

const CurrencyContext = createContext();

export const CurrencyProvider = ({ children }) => {
  const [selectedCurrency, setSelectedCurrency] = useState('GBP');
  const [detectedCurrency, setDetectedCurrency] = useState(null);
  const [showCurrencyPrompt, setShowCurrencyPrompt] = useState(false);

  // Load from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('selectedCurrency');
    if (saved && currencies.find(c => c.code === saved)) {
      setSelectedCurrency(saved);
    }
  }, []);

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem('selectedCurrency', selectedCurrency);
  }, [selectedCurrency]);

  const currency = getCurrency(selectedCurrency);

  // Convert price from GBP to selected currency
  const convertPrice = (priceInGBP) => {
    return convertFromGBP(priceInGBP, selectedCurrency);
  };

  // Format price with selected currency
  const formatPrice = (priceInGBP) => {
    const converted = convertPrice(priceInGBP);
    return formatPriceUtil(converted, selectedCurrency);
  };

  // Detect and set currency based on country code
  const detectCurrencyByCountry = useCallback((countryCode) => {
    const detectedCurr = getCurrencyByCountry(countryCode);
    if (detectedCurr && detectedCurr.code !== selectedCurrency) {
      setDetectedCurrency(detectedCurr);
      setShowCurrencyPrompt(true);
      // Auto-apply the detected currency
      setSelectedCurrency(detectedCurr.code);
    }
  }, [selectedCurrency]);

  // Dismiss the currency prompt
  const dismissCurrencyPrompt = useCallback(() => {
    setShowCurrencyPrompt(false);
    setDetectedCurrency(null);
  }, []);

  // Change currency and dismiss prompt
  const changeCurrency = useCallback((code) => {
    setSelectedCurrency(code);
    setShowCurrencyPrompt(false);
    setDetectedCurrency(null);
  }, []);

  return (
    <CurrencyContext.Provider value={{
      selectedCurrency,
      setSelectedCurrency: changeCurrency,
      currency,
      currencies,
      convertPrice,
      formatPrice,
      detectCurrencyByCountry,
      detectedCurrency,
      showCurrencyPrompt,
      dismissCurrencyPrompt
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
